import {QuestionCircleFilled} from '@ant-design/icons';
import {Button, Flex, Table, Tag, Tooltip, Typography} from 'antd';
import {ColumnsType} from 'antd/lib/table';
import React from 'react';
import {Link, useNavigate} from 'react-router-dom';

import {useAdminGroupSaleListQuery} from '../../../gen/api/apis';
import {AdminGroupSaleList, AdminGroupSaleMarketplace} from '../../../gen/api/models';
import {useNotification} from '../../../NotificationContext';
import {groupSaleStatusBadge, groupSaleStatusLabel, marketplaceLabel} from '../../helpers/status';
import {GroupSaleCreateModal} from './GroupSaleCreateModal';

export const GroupSaleList = () => {
    const notify = useNotification();
    const navigate = useNavigate();
    const [createModalVisible, setCreateModalVisible] = React.useState(false);

    const groupSalesQuery = useAdminGroupSaleListQuery({
        refetchOnMount: 'always',
    });

    const columns: ColumnsType<AdminGroupSaleList> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (name, item) => (
                <>
                    <Link to={`/group-sales/${item.id}`}>{name}</Link>
                </>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                return <Tag color={groupSaleStatusBadge(status)}>{groupSaleStatusLabel(status)}</Tag>;
            },
        },
        {
            title: (
                <>
                    PP{' '}
                    <Tooltip
                        placement="right"
                        title="Purchace Price"
                    >
                        <QuestionCircleFilled />
                    </Tooltip>
                </>
            ),
            dataIndex: 'pp',
            key: 'pp',
            render: (_, gb) => {
                if (!gb.products) {
                    return <span>-</span>;
                }
                return <span>${gb.products.reduce((acc, p) => acc + Number(p.purchasePrice), 0)}</span>;
            },
        },
        {
            title: 'MSRP',
            dataIndex: 'msrp',
            key: 'msrp',
            render: (_, gb) => {
                if (!gb.products) {
                    return <span>-</span>;
                }
                return <span>${gb.products.reduce((acc, p) => acc + Number(p.msrp), 0)}</span>;
            },
        },
        {
            title: (
                <>
                    LRP{' '}
                    <Tooltip
                        placement="right"
                        title="Lowest Resale Price"
                    >
                        <QuestionCircleFilled />
                    </Tooltip>
                </>
            ),
            dataIndex: 'lrp',
            key: 'lrp',
            render: (_, gb) => {
                if (!gb.products) {
                    return <span>-</span>;
                }
                return <span>${gb.products.reduce((acc, p) => acc + Number(p.lowestResalePrice), 0)}</span>;
            },
        },
        {
            title: 'Sold',
            dataIndex: 'sold_price',
            key: 'sold_price',
            render: (soldPrice, item) => {
                if (!soldPrice || !item.soldDate) {
                    return <span>-</span>;
                }
                return (
                    <span>
                        {soldPrice} {new Date(item.soldDate).toLocaleDateString()}
                    </span>
                );
            },
        },
        {
            title: 'Marketplaces',
            dataIndex: 'marketplaces',
            key: 'marketplaces',
            render: (marketplaces?: Array<AdminGroupSaleMarketplace>) => {
                return (
                    <Flex>
                        {marketplaces?.map((marketplace) => (
                            <Tag key={marketplace.id}>
                                {marketplaceLabel(marketplace.type)} - ${marketplace.price}
                            </Tag>
                        ))}
                    </Flex>
                );
            },
        },
        {
            title: 'Products',
            dataIndex: 'products',
            key: 'products',
            render: (_, gp) => {
                if (!gp.products || gp.products.length === 0) {
                    return <span>-</span>;
                }
                // Count of products
                return <span>{gp.products.length}</span>;
            },
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (createdAt) => <span>{new Date(createdAt).toLocaleDateString()}</span>,
        },
    ];

    return (
        <>
            <GroupSaleCreateModal
                open={createModalVisible}
                onEnd={() => {
                    setCreateModalVisible(false);
                    groupSalesQuery.refetch();
                }}
            />
            <Flex
                align={'center'}
                style={{marginBottom: '1rem'}}
            >
                <Typography.Title
                    level={3}
                    style={{margin: '0 10px 0 0'}}
                >
                    Group Sale List
                </Typography.Title>
                <Button
                    type="primary"
                    onClick={() => {
                        setCreateModalVisible(true);
                    }}
                >
                    Create
                </Button>
            </Flex>
            <Table<AdminGroupSaleList>
                rowKey={(record) => record.id}
                columns={columns}
                key={'id'}
                dataSource={groupSalesQuery.data?.groupSales}
                loading={groupSalesQuery.isLoading || groupSalesQuery.isLoading}
                scroll={{x: 'max-content'}}
                size={'small'}
                pagination={false}
            />
        </>
    );
};
